import { NgFor, NgIf } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { MetaDefinition, Title } from "@angular/platform-browser";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { ButtonComponent } from "rigshare-common";
import { LoaderDirective } from "rigshare-common";
import { WordpressService } from "rigshare-common";
import { SafeHtmlPipe } from "rigshare-common";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import {
  MetaService,
  WordPressAuthorModel,
  WordPressPostTagModel,
} from "rigshare-common";
import {
  WordPressPostCategoryModel,
  WordPressPostModel,
} from "rigshare-common";

@Component({
  selector: "app-blog",
  templateUrl: "./blog.component.html",
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    NgFor,
    LoaderDirective,
    ButtonComponent,
    SafeHtmlPipe,
  ],
})
export class BlogComponent implements OnInit {
  /**
   * The author of the posts.
   */
  author?: WordPressAuthorModel | null;

  /**
   * The category of the posts.
   */
  categories?: WordPressPostCategoryModel[] | null;

  /**
   * The category of the posts.
   */
  category?: WordPressPostCategoryModel | null;

  /**
   * The loading state of the component.
   */
  loading: boolean = false;

  /**
   * The meta tags of the page.
   */
  metaTags: MetaDefinition[] = [
    {
      name: "description",
      content:
        "Read about new features, product updates, and gear stories in the official RigShare blog",
    },
  ];

  /**
   * The page info of the component.
   */
  pageInfo: any;

  /**
   * The posts of the page.
   */
  posts: any[] = [];

  /**
   * The subscriptions of the component.
   */
  subs: Subscription = new Subscription();

  /**
   * The tag of the posts.
   */
  tag?: WordPressPostTagModel | null;

  /**
   * Create a new instance of the component.
   */
  constructor(
    private meta: MetaService,
    private route: ActivatedRoute,
    private title: Title,
    private wordpressService: WordpressService
  ) {}

  /**
   * On component init.
   */
  ngOnInit() {
    this.subscribeToQueryParams();
  }

  /**
   * On component destroy.
   */
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Get the selected category.
   */
  getAuthor(id: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.wordpressService.author(+id).subscribe({
        next: (data) => {
          this.author = new WordPressAuthorModel(data.author);
          resolve();
        },
        error: (error) => {
          console.error(error);
          reject();
        },
      });
    });
  }

  /**
   * Get the selected category.
   */
  getCategory(category: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const id = category.split("-").slice(-1)[0];

      this.wordpressService.category(+id).subscribe({
        next: (data) => {
          this.category = new WordPressPostCategoryModel(data.category);
          resolve();
        },
        error: (error) => {
          console.error(error);
          reject();
        },
      });
    });
  }

  /**
   * Get a list of categories.
   */
  async getCategories(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.wordpressService
        .categories({
          orderby: "count",
          order: "desc",
          hide_empty: "true",
        })
        .subscribe({
          next: (data) => {
            this.categories = data.categories.map(
              (category: any) => new WordPressPostCategoryModel(category)
            );

            resolve();
          },
          error: (error) => {
            console.error(error);
            reject();
          },
        });
    });
  }

  /**
   * Get posts from the api.
   */
  getPosts(params: any = {}): Promise<void> {
    return new Promise((resolve, reject) => {
      this.loading = true;

      params = {
        ...params,
        _embed: "true",
      };

      this.wordpressService
        .posts(params)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: (data) => {
            this.posts = data?.posts.map((p: any) => new WordPressPostModel(p));
            this.pageInfo = data?.pageInfo;
            resolve();
          },
          error: (error) => {
            console.error(error);
            reject(error);
          },
        });
    });
  }

  /**
   * Get the selected tag.
   */
  getTag(category: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const id = category.split("-").slice(-1)[0];

      this.wordpressService.tag(+id).subscribe({
        next: (data) => {
          this.tag = new WordPressPostTagModel(data.tag);
          resolve();
        },
        error: (error) => {
          console.error(error);
          reject();
        },
      });
    });
  }

  /**
   * Merge query params.
   */
  queryParamsMerge(params: object = {}) {
    return {
      ...this.route.snapshot.queryParams,
      ...params,
    };
  }

  /**
   * Return query params without a key.
   */
  queryParamsWithout(key: string) {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams[key];

    return queryParams[key];
  }

  /**
   * Setup the meta data for the page.
   */
  setMeta(): void {
    if (this.category) {
      if (this.category?.custom_fields?.title_tag) {
        this.title.setTitle(
          `${this.category?.custom_fields?.title_tag} | RigShare`
        );
      } else {
        this.title.setTitle(
          `Posts in the category: ${this.category.name} | RigShare`
        );
      }
    } else if (this.tag) {
      if (this.tag?.custom_fields?.title_tag) {
        this.title.setTitle(`${this.tag?.custom_fields?.title_tag} | RigShare`);
      } else {
        this.title.setTitle(`Posts tagged with ${this.tag.name} | RigShare`);
      }
    } else {
      this.title.setTitle("Blog | RigShare");
    }

    if (this.category?.custom_fields?.description_tag) {
      this.meta.create([
        {
          name: "description",
          content: this.category?.custom_fields?.description_tag,
        },
      ]);
    } else if (this.tag?.custom_fields?.description_tag) {
      this.meta.create([
        {
          name: "description",
          content: this.tag?.custom_fields?.description_tag,
        },
      ]);
    } else {
      this.meta.create(this.metaTags);
    }
  }

  /**
   * Subscribe to query param changes and fetch new posts.
   */
  subscribeToQueryParams(): void {
    const queryParamsSub = this.route.queryParams.subscribe({
      next: async (params) => {
        const promises = [];
        params = this.transformParams(params);

        if (params.author) {
          promises.push(this.getAuthor(params.author));
        } else {
          this.author = null;
        }

        if (params.categories) {
          promises.push(this.getCategory(params.categories));
        } else {
          promises.push(this.getCategories());
          this.category = null;
        }

        if (params.tags) {
          promises.push(this.getTag(params.tags));
        } else {
          this.tag = null;
        }

        promises.push(this.getPosts(params));

        try {
          await Promise.all(promises);
        } catch (error) {
          console.error(error);
        }

        this.setMeta();
      },
    });

    this.subs.add(queryParamsSub);
  }

  /**
   * Transform the query parameters that are sent to the WordPress API to use
   * the numeric IDs instead of the slugs.
   */
  transformParams(params: any): any {
    const paramsToTransform = ["categories", "tags"];
    const transformedParams: any = {};

    Object.keys(params).forEach((key) => {
      if (paramsToTransform.includes(key)) {
        transformedParams[key] = params[key].split("-").slice(-1)[0];
      } else {
        transformedParams[key] = params[key];
      }
    });

    return transformedParams;
  }
}
